import React, { useEffect, useRef, useState } from 'react'
import moment, { Moment } from 'moment'
import { connect } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Card, CardBody } from 'reactstrap'
import SimpleLoader from '../../../components/SimpleLoader'
import ServiceDetailLayout from '../../../components/Services/ServiceDetailLayout'
import ServiceDetailRenderer from '../../../components/Services/ServiceDetailRenderer'
import {
    normalizeServiceDetail,
    normalizeServiceGraphHistory,
    normalizeServiceUsage,
} from '../../../helpers/services/detail'
import {
    getServiceDetailIncludes,
    isServiceType,
    SERVICE_KINDS,
    TServiceDetail,
    TServiceGraphHistory,
    TNormalizedService,
    TServiceUsageData,
    USAGE_SUPPORTED_SERVICE_TYPES,
} from '../../../types/serviceView'
import { setSystemError } from '../../../store/system/actions'
import { AppDispatch } from '../../../configureStore'
import { IUserState } from '../../../store/user/types'
import { IServicesState } from '../../../store/services/types'
import {
    getService,
    getServiceGraph,
    getServiceUsage,
} from '../../../utils/Services/client'
import { navigateBackOrFallback } from '../../../helpers/navigation'
import { isRecord } from '../../../helpers/services/common'
import { checkPermission } from '../../../utils/Auth/AuthService'
import { getServiceGroups } from '../../../store/services/actions'
import { resolveServiceGroupName } from '../../../helpers/services/normalize'

type TOwnProps = {
    dispatch: AppDispatch
    user: IUserState
    services: Pick<IServicesState, 'serviceGroups'>
}

type TRouteParams = {
    id?: string
}

type TServiceLocationState = {
    from?: string
    service?: TNormalizedService | null
} | null
type TApiResult = {
    status?: number
    data?: unknown
}
type TGraphDateRangeHandler = React.ComponentProps<
    typeof ServiceDetailRenderer
>['onGraphDateRangeChange']

const SERVICE_DETAIL_LOADING_MIN_HEIGHT = 'calc(100vh - 260px)'

const getInitialUsagePeriod = (): { month: string; year: string } => ({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
})

const getInitialGraphRange = (): { startDate: Moment; endDate: Moment } => ({
    startDate: moment().startOf('day'),
    endDate: moment(),
})

const extractErrorMessage = (result: TApiResult, fallback: string): string => {
    if (typeof result?.data === 'string' && result.data) {
        return result.data
    }

    if (
        isRecord(result?.data) &&
        typeof result.data.error === 'string' &&
        result.data.error
    ) {
        return result.data.error
    }

    if (
        isRecord(result?.data) &&
        isRecord(result.data.error) &&
        typeof result.data.error.message === 'string' &&
        result.data.error.message
    ) {
        return result.data.error.message
    }

    return fallback
}

const shouldFetchUsage = (service: TServiceDetail): boolean =>
    service.resolvedKind === SERVICE_KINDS.BROADBAND &&
    isServiceType(service.type) &&
    USAGE_SUPPORTED_SERVICE_TYPES.has(service.type)

const isUnsupportedUsageResponse = (result: TApiResult): boolean => {
    if (result?.status !== 422) {
        return false
    }

    const productErrors = isRecord(result?.data)
        ? result.data.product
        : undefined
    return (
        Array.isArray(productErrors) &&
        productErrors.some((message) =>
            String(message).toLowerCase().includes('invalid product type'))
    )
}

const ServiceGenericDetail: React.FC<TOwnProps> = ({
    dispatch,
    user,
    services,
}) => {
    const { id } = useParams<TRouteParams>()
    const serviceId = id || ''
    const navigate = useNavigate()
    const location = useLocation()
    const locationState = location.state as TServiceLocationState
    const previousService = locationState?.service || null
    const fallbackBackPath = locationState?.from || '/services'
    const canReadBilling = checkPermission('billing.read', user.permissions)
    const showBillingDetails = canReadBilling

    const getResolvedGroupName = (group?: number | string): string | undefined =>
        resolveServiceGroupName(
            group,
            services.serviceGroups,
            previousService?.groupName
        )

    const [loading, setLoading] = useState<boolean>(true)
    const [service, setService] = useState<TServiceDetail | null>(null)
    const [detailError, setDetailError] = useState<string>('')

    const [usagePeriod, setUsagePeriod] = useState(getInitialUsagePeriod())
    const [usage, setUsage] = useState<TServiceUsageData | null>(null)
    const [usageLoading, setUsageLoading] = useState<boolean>(false)
    const [usageError, setUsageError] = useState<string>('')
    const [usageEnabled, setUsageEnabled] = useState<boolean>(false)

    const [selectedGraphId, setSelectedGraphId] = useState<string>('')
    const [graphRange, setGraphRange] = useState(getInitialGraphRange())
    const [graphHistory, setGraphHistory] =
        useState<TServiceGraphHistory | null>(null)
    const [graphLoading, setGraphLoading] = useState<boolean>(false)
    const [graphError, setGraphError] = useState<string>('')

    const graphCache = useRef<Map<string, TServiceGraphHistory>>(new Map())
    const graphRequests = useRef<Set<string>>(new Set())
    const latestGraphRequestKey = useRef<string>('')

    useEffect(() => {
        if (services.serviceGroups.length === 0) {
            void dispatch(getServiceGroups())
        }
    }, [dispatch, services.serviceGroups.length])

    useEffect(() => {
        if (!service) {
            return
        }

        const nextState = {
            from: locationState?.from || fallbackBackPath,
            service,
        }

        if (
            locationState?.from === nextState.from &&
            locationState?.service?.id === service.id &&
            locationState?.service?.productCode === service.productCode &&
            locationState?.service?.groupName === service.groupName
        ) {
            return
        }

        navigate(location.pathname, {
            replace: true,
            state: nextState,
        })
    }, [fallbackBackPath, location.pathname, locationState, navigate, service])

    useEffect(() => {
        let cancelled = false

        if (!serviceId) {
            setLoading(false)
            setDetailError('Service detail is unavailable.')
            return () => {
                cancelled = true
            }
        }

        setLoading(true)
        setDetailError('')
        setService(null)
        setUsage(null)
        setUsageError('')
        setUsageEnabled(false)
        setGraphHistory(null)
        setGraphError('')
        setSelectedGraphId('')
        graphCache.current.clear()
        graphRequests.current.clear()

        getService(serviceId, getServiceDetailIncludes(canReadBilling)).then(
            (result) => {
                if (cancelled) {
                    return
                }

                if (result.status === 200) {
                    const normalizedService = normalizeServiceDetail(
                        result.data,
                        {
                            previousService,
                            groupName: getResolvedGroupName(
                                isRecord(result.data) &&
                                    (typeof result.data.group === 'number' ||
                                        typeof result.data.group === 'string')
                                    ? result.data.group
                                    : undefined
                            ),
                        }
                    )
                    setService(normalizedService)
                    setUsageEnabled(shouldFetchUsage(normalizedService))
                    setSelectedGraphId(normalizedService.graphs[0]?.id || '')
                } else {
                    if (
                        isRecord(result?.data) &&
                        isRecord(result.data.error) &&
                        typeof result.data.error.id === 'string'
                    ) {
                        dispatch(setSystemError(result.data.error.id))
                    }

                    setDetailError(
                        extractErrorMessage(
                            result,
                            'Unable to load this service.'
                        )
                    )
                }

                setLoading(false)
            }
        )

        return () => {
            cancelled = true
        }
    }, [canReadBilling, dispatch, previousService, serviceId, services.serviceGroups])

    useEffect(() => {
        if (!service || service.groupName || services.serviceGroups.length === 0) {
            return
        }

        const nextGroupName = getResolvedGroupName(service.group)
        if (!nextGroupName) {
            return
        }

        setService((currentService) =>
            currentService
                ? {
                      ...currentService,
                      groupName: nextGroupName,
                  }
                : currentService)
    }, [
        previousService?.groupName,
        service,
        services.serviceGroups,
    ])

    useEffect(() => {
        if (!service || !usageEnabled) {
            setUsageLoading(false)
            setUsageError('')
            setUsage(null)
            return
        }

        let cancelled = false
        setUsageLoading(true)
        setUsageError('')

        getServiceUsage(service.id, usagePeriod.month, usagePeriod.year).then(
            (result) => {
                if (cancelled) {
                    return
                }

                if (result.status === 200) {
                    setUsage(normalizeServiceUsage(result.data))
                } else if (isUnsupportedUsageResponse(result)) {
                    setUsageEnabled(false)
                    setUsage(normalizeServiceUsage(null))
                    setUsageError('')
                } else {
                    setUsage(normalizeServiceUsage(null))
                    setUsageError(
                        extractErrorMessage(
                            result,
                            'Unable to load usage for the selected period.'
                        )
                    )
                }

                setUsageLoading(false)
            }
        )

        return () => {
            cancelled = true
        }
    }, [service, usageEnabled, usagePeriod.month, usagePeriod.year])

    useEffect(() => {
        if (!service || !selectedGraphId) {
            setGraphHistory(null)
            setGraphError('')
            setGraphLoading(false)
            return
        }

        const selectedGraph = service.graphs.find(
            (graph) => graph.id === selectedGraphId
        )
        if (!selectedGraph) {
            setGraphHistory(null)
            setGraphError('')
            setGraphLoading(false)
            return
        }

        const start = graphRange.startDate.unix()
        const end = graphRange.endDate.unix()
        const requestKey = `${service.id}:${selectedGraphId}:${start}:${end}`
        latestGraphRequestKey.current = requestKey

        if (graphCache.current.has(requestKey)) {
            setGraphHistory(graphCache.current.get(requestKey) || null)
            setGraphError('')
            setGraphLoading(false)
            return
        }

        if (graphRequests.current.has(requestKey)) {
            return
        }

        let cancelled = false
        graphRequests.current.add(requestKey)
        setGraphLoading(true)
        setGraphError('')

        getServiceGraph(service.id, selectedGraphId, start, end).then(
            (result) => {
                graphRequests.current.delete(requestKey)

                if (cancelled) {
                    return
                }

                if (result.status === 200) {
                    const normalizedGraphHistory = normalizeServiceGraphHistory(
                        result.data,
                        selectedGraph
                    )
                    graphCache.current.set(requestKey, normalizedGraphHistory)

                    if (latestGraphRequestKey.current === requestKey) {
                        setGraphHistory(normalizedGraphHistory)
                    }
                } else if (latestGraphRequestKey.current === requestKey) {
                    setGraphHistory(null)
                    setGraphError(
                        extractErrorMessage(
                            result,
                            'Unable to load graph history for the selected range.'
                        )
                    )
                }

                if (latestGraphRequestKey.current === requestKey) {
                    setGraphLoading(false)
                }
            }
        )

        return () => {
            cancelled = true
        }
    }, [graphRange.endDate, graphRange.startDate, selectedGraphId, service])

    const onBack = () =>
        navigateBackOrFallback(navigate, locationState, fallbackBackPath)

    const onMonthChange = (month: string) => {
        if (/^(0[1-9]|1[0-2])$/.test(month)) {
            setUsagePeriod((currentValue) => ({ ...currentValue, month }))
        }
    }

    const onYearChange = (year: string) => {
        if (/^[0-9]{4}$/.test(year)) {
            setUsagePeriod((currentValue) => ({ ...currentValue, year }))
        }
    }

    const onGraphDateRangeChange: TGraphDateRangeHandler = (event, picker) => {
        if (event.type === 'apply') {
            setGraphRange({
                startDate: picker.startDate.clone().startOf('day'),
                endDate: picker.endDate.clone().endOf('day'),
            })
        }
    }

    if (!service && !loading) {
        return (
            <div
                className="animated fadeIn mb-5"
                style={{ minHeight: SERVICE_DETAIL_LOADING_MIN_HEIGHT }}
            >
                <Card className="shadow-sm border-0">
                    <CardBody>
                        {detailError || 'Service detail is unavailable.'}
                    </CardBody>
                </Card>
            </div>
        )
    }

    return (
        <SimpleLoader loading={loading} text="Loading service details...">
            {service ? (
                <ServiceDetailLayout service={service} onBack={onBack}>
                    {detailError ? (
                        <Card className="shadow-sm border-0">
                            <CardBody className="text-danger">
                                {detailError}
                            </CardBody>
                        </Card>
                    ) : (
                        <ServiceDetailRenderer
                            service={service}
                            showBillingDetails={showBillingDetails}
                            usage={usage}
                            usageLoading={usageLoading}
                            usageError={usageError}
                            month={usagePeriod.month}
                            year={usagePeriod.year}
                            onMonthChange={onMonthChange}
                            onYearChange={onYearChange}
                            selectedGraphId={selectedGraphId}
                            onGraphChange={setSelectedGraphId}
                            graphHistory={graphHistory}
                            graphLoading={graphLoading}
                            graphError={graphError}
                            graphStartDate={graphRange.startDate}
                            graphEndDate={graphRange.endDate}
                            onGraphDateRangeChange={onGraphDateRangeChange}
                        />
                    )}
                </ServiceDetailLayout>
            ) : (
                <div
                    className="animated fadeIn mb-5"
                    style={{ minHeight: SERVICE_DETAIL_LOADING_MIN_HEIGHT }}
                />
            )}
        </SimpleLoader>
    )
}

const mapStateToProps = (state: {
    user: IUserState
    services: IServicesState
}) => ({
    user: state.user,
    services: state.services,
})

export default connect(mapStateToProps)(ServiceGenericDetail)
